import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { format } from 'date-fns';
import { AppointmentStatus, TattooChoices } from '@prisma/client';
import { Box, Skeleton } from '@mui/material';
import {
  Appointment,
  EmployeeToBusiness,
  GeneralRequest,
  LoadedUser,
  ModalData,
  Row,
} from '../../interfaces';
import { createModalData } from '../modal/requestModal';
import { HeaderCells } from '../../types/ui';
import HorizontalCardList from '../../components/global/HorizontalCardList';
import {
  getCustomerNameFromAppointment,
  getEmployeeNameFromAppointmentOrGenRequest,
} from '../global/helper';
import RequestImage from '../../components/appointments/RequestImage';

export const headerCells = [
  { id: 'images', label: 'Images' },
  { id: 'employee', label: 'Artist' },
  { id: 'customer', label: 'Client' },
  { id: 'tattooLocation', label: 'Tattoo Location' },
  { id: 'createdAt', label: 'Submitted On' },
  { id: 'details', label: 'View Details' },
] as const satisfies HeaderCells;

// Function to create a table row from an appointment data
function createRow({
  appointment,
  handleClick,
  sizeOptions,
  navigateToCustomer,
  allBusinesses,
  isUnread,
  employeesToBusiness,
}: {
  appointment: Appointment | GeneralRequest;
  handleClick: (
    newModalData: ModalData,
    appointment: Appointment | GeneralRequest,
  ) => void;
  sizeOptions: TattooChoices[] | undefined;
  navigateToCustomer: (customerId: string) => void;
  allBusinesses: LoadedUser['allBusinesses'];
  isUnread?: boolean;
  employeesToBusiness?: EmployeeToBusiness[];
}) {
  const employeeName = getEmployeeNameFromAppointmentOrGenRequest(
    appointment,
    employeesToBusiness,
  );

  const newModalData = createModalData({
    appointment,
    sizeOptions,
    navigateToCustomer,
    allBusinesses,
    employeesToBusiness,
  });

  const row = {
    rowId:
      (appointment as Appointment).appointmentId ||
      (appointment as GeneralRequest).requestId,
    cells: [
      <RequestImage image={appointment.refImgsId?.[0]} isUnread={isUnread} />,
      <Typography variant="body1">{employeeName}</Typography>,
      <Typography variant="body1">
        {getCustomerNameFromAppointment(appointment)}
      </Typography>,
      <Typography variant="body1">
        {appointment.tattooLocation
          ? appointment.tattooLocation
          : 'Not provided'}
      </Typography>,
      <Typography variant="body1">
        {format(new Date(appointment.createdAt!), 'MMMM d, y')}
      </Typography>,
      <Button
        onClick={() => handleClick(newModalData, appointment)}
        variant="outlined"
        className="table-button">
        View Request
      </Button>,
    ],
  };

  return row;
}

// Function to create filtered rows based on appointment status
export function createRows({
  items,
  status,
  handleClick,
  sizeOptions,
  navigateToCustomer,
  unreadRequests,
  allBusinesses,
  employeesToBusiness,
}: {
  items: (Appointment | GeneralRequest)[] | undefined;
  status: string;
  handleClick: (
    newModalData: ModalData,
    appointment: Appointment | GeneralRequest,
  ) => void;
  sizeOptions: TattooChoices[] | undefined;
  navigateToCustomer: (customerId: string) => void;
  unreadRequests?: {
    generalRequests: string[];
    appointments: string[];
  };
  allBusinesses: LoadedUser['allBusinesses'];
  employeesToBusiness?: EmployeeToBusiness[];
}) {
  if (!items) {
    return [];
  }

  const rows = (items ?? [])
    .map(item => {
      if (
        (item as Appointment).status &&
        (item as Appointment).status === status
      ) {
        const isApptUnread = unreadRequests?.appointments.includes(
          (item as Appointment).appointmentId,
        );
        return createRow({
          appointment: item,
          handleClick,
          sizeOptions,
          navigateToCustomer,
          allBusinesses,
          isUnread: isApptUnread,
        });
      }
      if (
        (item as GeneralRequest).requestId &&
        (status === 'REQUESTED' || status === 'ARCHIVED')
      ) {
        const isGenReqUnread = unreadRequests?.generalRequests.includes(
          (item as GeneralRequest).requestId,
        );
        return createRow({
          appointment: item,
          handleClick,
          sizeOptions,
          navigateToCustomer,
          allBusinesses,
          isUnread: isGenReqUnread,
          employeesToBusiness,
        });
      }
      return null;
    })
    .filter(Boolean) as Row[];

  return rows;
}

// Function to create filtered rows based on appointment status
export function createCardList({
  items,
  status,
  handleClick,
  loading,
  sizeOptions,
  unreadRequests,
}: {
  items: (Appointment | GeneralRequest)[] | undefined;
  status: AppointmentStatus;
  handleClick: (
    newModalData: ModalData,
    appointment: Appointment | GeneralRequest,
  ) => void;
  loading: boolean;
  sizeOptions: TattooChoices[] | undefined;
  unreadRequests?: {
    generalRequests: string[];
    appointments: string[];
  };
}) {
  if (!items && !loading) {
    return [];
  }

  if (loading) {
    return (
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
          gap: 10,
        }}>
        <Skeleton
          variant="rounded"
          width="140px"
          height="140px"
          sx={{ flexShrink: 0 }}
        />
        <Skeleton
          variant="rounded"
          width="140px"
          height="140px"
          sx={{ flexShrink: 0 }}
        />
        <Skeleton
          variant="rounded"
          width="140px"
          height="140px"
          sx={{ flexShrink: 0 }}
        />
      </Box>
    );
  }

  if (items?.length === 0) {
    return (
      <Typography variant="body1">
        {`No ${
          status === 'REQUESTED' ? 'new' : status.toLowerCase()
        } requests to show right now. If you believe this is an error, please reach out to our support team.`}
      </Typography>
    );
  }

  const appointments = (items ?? [])
    .map(item => {
      if (
        (item as Appointment).status &&
        (item as Appointment).status === status
      ) {
        return item;
      }
      if ((item as GeneralRequest).requestId && status === 'REQUESTED') {
        return item;
      }
      return null;
    })
    .filter(Boolean) as (Appointment | GeneralRequest)[];

  return (
    <HorizontalCardList
      appointments={appointments}
      handleClick={handleClick}
      sizeOptions={sizeOptions}
      unreadRequests={unreadRequests}
    />
  );
}
